import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import { Box, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import { cloneDeep, isObject } from "lodash";
import { v4 as uuidv4 } from "uuid";

import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

import GlobalAlert from "components/Notification";
import SearchInput from "components/SearchInput";
import TableField from "components/Table";
import { SolidButton } from "components/buttons/SolidButton";
import ConfirmDeleteModal from "./Modal/ConfirmDeleteModal";
import Loading from "./loading";
import ProfileUserAccountModal from "./Modal";

import Api from "./util/api";
import { checkEnvironment, checkMockup } from "./util/const";
import { fromJSON, getTableColumns } from "./util/func";

const ProfileUserAccount = ({ showEditGeneral }) => {
  const theme = useTheme();
  const columnHelper = createColumnHelper();
  const { scopeReady } = useOutletContext();
  const location = useLocation();
  const level = location.pathname.split("/")[1];
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [loading, setLoading] = useState(false);
  const [auth, setAuth] = useState({
    scopeLevel: null,
    realm: null,
    orgs: null,
    sites: null,
    permissions: null,
  });
  const [uuidList] = useState({
    read: uuidv4(),
    create: uuidv4(),
    update: uuidv4(),
    delete: uuidv4(),
  });
  const [originUserAccountProfiles, setOriginUserAccountProfiles] = useState([]);
  const [userAccountProfiles, setUserAccountProfiles] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [selectedUserAccountProfile, setSelectedUserAccountProfile] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const pendingRequestRef = useRef({
    delete: false,
  });

  const fetchTableData = async () => {
    setLoading(true);
    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.read);

    sendRequest(apiKey, uuidList.read, params);
  };

  const handleSearchChange = (string) => {
    setSearchVal(string);

    if (string.trim() === "") {
      setUserAccountProfiles(cloneDeep(originUserAccountProfiles));
      return;
    }

    const keyword = string.toLowerCase();
    const filteredData = originUserAccountProfiles.filter((item) =>
      item.profileName.toLowerCase().includes(keyword)
    );

    setUserAccountProfiles(filteredData);
  };

  const createAction = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedUserAccountProfile(null);
    setOpenModal(false);
  };

  const handleSaveChanges = () => {
    handleCloseModal();
    fetchTableData();
  };

  const editUserAccountProfile = (data) => {
    setSelectedUserAccountProfile(data);
    setOpenModal(true);
  };

  const deleteUserAccountProfile = (data) => {
    setSelectedUserAccountProfile(data);
    setOpenRemoveModal(true);
  };

  const handleCloseRemoveModal = () => {
    setSelectedUserAccountProfile(null);
    setOpenRemoveModal(false);
  };

  const handleDeleteUserAccountProfile = () => {
    setLoading(true);
    pendingRequestRef.current.delete = true;
    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.delete);

    sendRequest(apiKey, uuidList.delete, params, {
      profileUserAccountsIds: [selectedUserAccountProfile.id],
    });

    handleCloseRemoveModal();
  };

  useEffect(() => {
    setAuth((prev) => ({
      ...prev,
      scopeLevel: level,
    }));
  }, []);

  useEffect(() => {
    if (scopeReady || checkMockup === "true") {
      const { realm, orgs, site } = JSON.parse(
        localStorage.getItem("selectBreadcrumbs")
      );

      if (isObject(site) && Object.keys(site).length > 0) {
        setAuth((prev) => ({
          ...prev,
          scopeLevel: "site",
          realm,
          orgs,
          sites: site,
          permissions: site.roleName,
        }));
      } else {
        const currentPermissions = checkEnvironment === "premises" ? realm.roleName : orgs.roleName;
        setAuth((prev) => ({
          ...prev,
          scopeLevel: "org",
          realm,
          orgs,
          permissions: currentPermissions,
        }));
      }
    }
  }, [scopeReady]);

  useEffect(() => {
    if (!auth.scopeLevel) return;
    fetchTableData();
  }, [auth.scopeLevel, auth.realm, auth.orgs, auth.sites]);

  useEffect(() => {
    const readResponse = getData(uuidList.read);

    if (readResponse) {
      if (readResponse.hasOwnProperty("code")) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: readResponse?.message,
        });
        setLoading(false);
      } else {
        const updatedUserAccountProfiles = [];
        const data = readResponse.data || [];

        for (let i = 0; i < data.length; i++) {
          updatedUserAccountProfiles.push(fromJSON(data[i]));
        }

        setOriginUserAccountProfiles(cloneDeep(updatedUserAccountProfiles));
        setUserAccountProfiles(updatedUserAccountProfiles);
        setLoading(false);
      }
    }
  }, [dataMap?.[uuidList.read]]);

  useEffect(() => {
    const deleteResponse = getData(uuidList.delete);

    if (deleteResponse && pendingRequestRef.current.delete) {
      pendingRequestRef.current.delete = false;

      if (deleteResponse.hasOwnProperty("code") && deleteResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: deleteResponse?.message,
        });
        setLoading(false);
      } else {
        fetchTableData();
      }
    }
  }, [dataMap?.[uuidList.delete]]);

  const columnsData = getTableColumns(
    theme,
    columnHelper,
    showEditGeneral,
    editUserAccountProfile,
    deleteUserAccountProfile
  );
  const table = useTableInstance({
    tableName: "profilesUserAccountTable",
    data: userAccountProfiles,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ padding: "20px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "80px",
          gap: 3,
        }}
      >
        <Divider
          sx={{ width: "1px", height: "40px", margin: "20px 0" }}
          orientation="vertical"
          flexItem
        />

        <SearchInput
          value={searchVal}
          placeholder="Search by Name"
          onChange={handleSearchChange}
        />
        {showEditGeneral && (
          <SolidButton
            startIcon={<AddIcon />}
            width="240px"
            height="32px"
            onClick={createAction}
          >
            Create User Account Profile
          </SolidButton>
        )}
      </Box>

      <TableField
        {...table}
        pendingChildren={loading && <Loading />}
      />

      <ProfileUserAccountModal
        auth={auth}
        uuidList={uuidList}
        openModal={openModal}
        selectedUserAccountProfile={selectedUserAccountProfile}
        showEditGeneral={showEditGeneral}
        handleCloseModal={handleCloseModal}
        onSaveChanges={handleSaveChanges}
      />

      <ConfirmDeleteModal
        removeModal={openRemoveModal}
        onOpenRemoveModal={handleCloseRemoveModal}
        label={selectedUserAccountProfile ? selectedUserAccountProfile.profileName : ""}
        removeCallback={handleDeleteUserAccountProfile}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ProfileUserAccount;
